import React, { useEffect, useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Typography,
  FormControl,
  InputAdornment,
  Select,
  MenuItem,
  Chip,
  Grid,
  Avatar,
  CircularProgress
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { Add, Edit, Delete, Close, CloudUpload } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import {
  fetchPodcaster,
  createPodcaster,
  updatePodcaster,
  deletePodcaster
} from "../../Redux/Reducers/PodcasterReducer";
import { GlobalStyles, useTheme, useMediaQuery } from '@mui/material';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import usePermissions from "../../hooks/usePermissions";

const MODULE_KEY = "podcaster";

const EMPTY_FORM = {
  email: "",
  name: "",
  phone: "",
  bio: "",
  isActive: true,
  isVerified: false,
  image: null
};

function PodcasterManagement() {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation();
  const { hasAction } = usePermissions();
  const { podcasters, loading, pagination } = useSelector((state) => state.podcasters || {});
  console.log(podcasters, "podcasters")
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPodcaster, setEditingPodcaster] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const { setError } = useForm();

  // For Table Thead Width
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isLarge = useMediaQuery(theme.breakpoints.up('lg'));

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: EMPTY_FORM
  });

  const password = watch("password");

  useEffect(() => {
    dispatch(
      fetchPodcaster({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: searchText,
      })
    );
  }, [dispatch, paginationModel, searchText]);

  const podcasterList = useMemo(() => {
    if (!podcasters) return [];
    if (Array.isArray(podcasters)) return podcasters;
    if (podcasters.data && Array.isArray(podcasters.data)) return podcasters.data;
    return [];
  }, [podcasters]);

  const handleOpenModal = (row = null) => {
    if (row) {
      setEditingPodcaster(row);
      reset({
        name: row.name || "",
        email: row.email || "",
        phone: row.phone || "",
        bio: row.bio || "",
        isActive: row.isActive ?? true,
        isVerified: row.isVerified ?? false,
        image: null
      });

      setImagePreview(row?.profileImage ? `${row?.profileImage}` : null);
    } else {
      setEditingPodcaster(null);

      reset(EMPTY_FORM);

      setImagePreview(null);
    }
    setModalOpen(true);
  };

  /* ================= IMAGE HANDLER ================= */
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t('podcastersTranslation.validations.image_allowed', "Only image files allowed"));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error(t('podcastersTranslation.validations.image_size', "Image must be under 5MB"));
      return;
    }

    setValue("image", file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleCloseModal = () => setModalOpen(false);

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const payload = new FormData();
      payload.append("name", data.name);
      payload.append("email", data.email);
      payload.append("phone", data.phone);
      payload.append("bio", (data.bio));
      payload.append("isActive", data.isActive === true);
      payload.append("isVerified", data.isVerified === true);

      if (data.password) payload.append("password", data.password);
      if (data.image) payload.append("profileImage", data.image);

      if (editingPodcaster) {
        await dispatch(
          updatePodcaster({ id: editingPodcaster.id, formData: payload })
        ).unwrap();
        toast.success(t('podcastersTranslation.messages.update_success', 'Podcaster updated successfully'));
      } else {
        await dispatch(createPodcaster(payload)).unwrap();
        toast.success(t('podcastersTranslation.messages.create_success', 'Podcaster created successfully'));
      }
      handleCloseModal();
      dispatch(
        fetchPodcaster({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          search: searchText,
        })
      );
    } catch (err) {
      console.error(err);

      if (!err?.errors) {
        toast.error(err?.message || "Something went wrong");
      }

      // Handle field errors
      if (err?.errors) {
        Object.keys(err.errors).forEach((key) => {
          setError(key, {
            type: "server",
            message: err.errors[key],
          });
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await dispatch(deletePodcaster(selectedId)).unwrap();
      toast.success(t('podcastersTranslation.messages.delete_success', 'Podcaster deleted successfully'));
      dispatch(
        fetchPodcaster({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          search: searchText,
        })
      );
    } catch {
      toast.error(t('podcastersTranslation.messages.delete_failed', 'Delete failed'));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  const columns = [
    { field: "sno", headerName: t('sno', 'S.No'), width: 80, sortable: false },
    {
      field: "avatar",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => {
        return <Avatar variant="square" src={params.value} sx={{ width: 32, height: 32, borderRadius: '5px' }} />
      },
    },
    {
      field: "name",
      headerName: t('podcastersTranslation.fields.name', 'Name'),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
    },
    {
      field: "email",
      headerName: t('podcastersTranslation.fields.email', 'Email'),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
    },
    {
      field: "phone",
      headerName: t('podcastersTranslation.fields.phone', 'Phone'),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
    },
    {
      field: "isVerified",
      headerName: t('podcastersTranslation.fields.verified', 'Verified'),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) => (
        <Chip
          label={params.value ? t('verified', 'Verified') : t('pending', 'Pending')}
          color={params.value ? "success" : "warning"}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: "isActive",
      headerName: t('podcastersTranslation.fields.status', 'Status'),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) => (
        <Chip
          label={params.value ? t('active', 'Active') : t('inactive', 'Inactive')}
          color={params.value ? "success" : "error"}
          size="small"
          variant="outlined"
        />
      )
    },
    {
      field: "actions",
      headerName: t('actions', 'Actions'),
      width: 140,
      sortable: false,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: '6px', }}>
          {hasAction(MODULE_KEY, 'update') && (
            <IconButton onClick={() => handleOpenModal(params.row)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path fillRule="evenodd" clipRule="evenodd" d="M15.531 3.84226C13.9473 5.42594 10.2302 9.14231 9.3237 10.0459C9.12779 10.2411 8.89463 10.3952 8.64435 10.4958C7.91732 10.7975 6.14371 11.4932 5.62228 11.5283C5.31165 11.5491 5.00699 11.4344 4.7865 11.2139C4.566 10.9934 4.45129 10.6887 4.47215 10.3781C4.50716 9.85668 5.2029 8.08453 5.49937 7.36048C5.60291 7.10348 5.7571 6.87032 5.95301 6.67516L12.1581 0.469295C12.4582 0.169095 12.8665 0 13.291 0C13.7156 0 14.1239 0.169095 14.424 0.469295L15.531 1.57623C15.8312 1.87643 16.0003 2.28465 16.0003 2.70925C16.0003 3.13384 15.8312 3.54206 15.531 3.84226ZM5.59174 10.4087C6.10125 10.3446 7.59031 9.72334 8.21603 9.46336C8.21826 9.46262 8.2205 9.46187 8.22199 9.46113C8.33894 9.4142 8.44471 9.34418 8.53336 9.25553L8.5341 9.25479C9.44214 8.35121 13.1577 4.63559 14.7406 3.0519C14.8315 2.96103 14.8829 2.83811 14.8829 2.70925C14.8829 2.58038 14.8315 2.45746 14.7406 2.36659L13.6337 1.25965C13.5428 1.16877 13.4199 1.11737 13.291 1.11737C13.1622 1.11737 13.0393 1.16877 12.9484 1.25965L6.74261 7.46551C6.65322 7.55415 6.5832 7.66068 6.53627 7.77763L6.53478 7.78061C6.27779 8.40782 5.6558 9.89839 5.59174 10.4087Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M14.0542 4.52797C14.2717 4.74623 14.2717 5.10006 14.0542 5.31832C13.8359 5.53658 13.4821 5.53658 13.2638 5.31832L10.6813 2.73571C10.463 2.51745 10.463 2.16362 10.6813 1.94536C10.8995 1.72785 11.2534 1.72785 11.4716 1.94536L14.0542 4.52797Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M9.49485 9.08712C9.71311 9.30464 9.71311 9.65922 9.49485 9.87673C9.27734 10.095 8.92277 10.095 8.70525 9.87673L6.12268 7.29412C5.90442 7.07661 5.90442 6.72203 6.12268 6.50451C6.34019 6.28626 6.69476 6.28626 6.91227 6.50451L9.49485 9.08712Z" fill="white" />
                <path fillRule="evenodd" clipRule="evenodd" d="M5.40054 1.47412C5.70893 1.47412 5.95922 1.72441 5.95922 2.03281C5.95922 2.3412 5.70893 2.59149 5.40054 2.59149H2.79339C1.86747 2.59149 1.11735 3.34162 1.11735 4.26754V13.2065C1.11735 13.6512 1.2939 14.0773 1.60824 14.3916C1.92259 14.706 2.34868 14.8825 2.79339 14.8825H12.4771C12.9218 14.8825 13.3479 14.706 13.6623 14.3916C13.9766 14.0773 14.1532 13.6512 14.1532 13.2065V10.5993C14.1532 10.2909 14.4034 10.0406 14.7118 10.0406C15.0202 10.0406 15.2705 10.2909 15.2705 10.5993V13.2065C15.2705 13.9477 14.9763 14.6576 14.4526 15.182C13.9282 15.7057 13.2183 15.9999 12.4771 15.9999H2.79339C2.05221 15.9999 1.34231 15.7057 0.817903 15.182C0.294237 14.6576 0 13.9477 0 13.2065V4.26754C0 2.72483 1.25069 1.47412 2.79339 1.47412H5.40054Z" fill="white" />
              </svg>
            </IconButton>
          )}
          {hasAction(MODULE_KEY, 'delete') && (
            <IconButton onClick={() => handleDeleteClick(params.row.id)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}>
              <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white" />
                <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white" />
                <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white" />
                <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white" />
                <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white" />
              </svg>
            </IconButton>
          )}
        </Box>
      )
    }
  ];

  const rows = useMemo(() => {
    return podcasterList
      .map((podcaster, index) => ({
        id: podcaster.id || `temp-id-${index}`,
        sno: paginationModel.page * paginationModel.pageSize + index + 1,
        name: podcaster.name || "",
        email: podcaster.email || "",
        phone: podcaster.phone || "",
        bio: podcaster.bio || "",
        isVerified: podcaster.isVerified || false,
        isActive: podcaster.isActive,
        avatar: podcaster?.profileImage ? `${podcaster?.profileImage?.cdnUrl}` : null,
        profileImage: podcaster?.profileImage ? `${podcaster?.profileImage?.cdnUrl}` : null,
      }));
  }, [podcasterList, paginationModel.page, paginationModel.pageSize]);


  return (
    <Box sx={{ p: '36px 24px 24px' }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('podcastersTranslation.title', 'Podcaster Management')}</Typography>
        <Box display="flex" gap={2}>
          <TextField
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('search', 'Search')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '999px',
                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                color: '#fff',
                height: '38px',
                '& fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 16px',
                color: '#FF69B4',
                fontSize: '15px',
                lineHeight: '18px',

              },
              '& input::placeholder': {
                color: 'rgba(255,255,255,0.6)',
                opacity: 1,
              },
              '& .MuiFormHelperText-root': {
                ml: '20px'
              }
            }}
          />
          {hasAction(MODULE_KEY, 'create') && (
            <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal()}
              sx={{
                border: '1px solid rgba(230, 210, 249, 0.1) !important',
                color: '#fff',
                textTransform: 'none',
                backgroundColor: '#FF69B4 !important',
                borderRadius: '999px',
                fontSize: '15px',
                lineHeight: '24px',
                padding: '0',
                minWidth: '38px',
                '& .MuiButton-startIcon': {
                  margin: 0,
                },
              }}
            ></Button>
          )}
        </Box>
      </Box>

      <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar }}
          paginationMode="server"
          rowCount={pagination?.totalDocs ?? pagination?.total ?? 0}
          paginationModel={paginationModel}

          onPaginationModelChange={(model) => {
            setPaginationModel(model);
          }}
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true }, // Removes the "Export as CSV" menu item
              printOptions: { disableToolbarButton: true }, // Removes the "Print" menu item
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: "nameEn", sort: "asc" }] }
          }}
          getRowId={(row) => row.id || row.sno}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          localeText={i18n.language?.toLowerCase().startsWith('fr') ? frFR.components.MuiDataGrid.defaultProps.localeText : enUS.components.MuiDataGrid.defaultProps.localeText}
          sx={{
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '0',
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px',
              margin: '0 -12px',
              gap: '10px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-topContainer::after': {
              display: 'none',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
              color: '#fff',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
              width: '18px',
              height: '18px',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              maxHeight: 'auto',
              height: 'auto !important'
            },
            '& .MuiDataGrid-virtualScroller': {
              borderRadius: '0 !important',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              position: 'inherit',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
              maxHeight: 'none !important',
              height: 'auto !important',
            },
            '& .MuiDataGrid-cell': {
              height: 'auto !important',
              minHeight: 'var(--height)',
              paddingTop: '5px',
              paddingBottom: '5px',
            },
            '& .MuiDataGrid-cell>.MuiBox-root': {
              minHeight: '100%',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
              borderRadius: '0 !important',
              borderColor: 'rgba(230, 210, 249, 0.2) !important',
              color: '#fff',
            },
            '& .MuiDataGrid-columnHeaders [role=row]': {
              backgroundColor: '#1a1a1a',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'rgba(230, 210, 249, 0.2) !important'
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
              color: 'white',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              opacity: '0.8 !important',
              fill: 'white',
            },
            '& .MuiDataGrid-cell .MuiChip-outlined': {
              border: '0 !important',
              height: 'auto',
              fontWeight: '600',
            },
            '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
              color: "#03c11c",
            },
            '& .MuiTablePagination-root': {
              color: '#fff'
            },
            '& .MuiTablePagination-selectLabel': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-input': {
              marginRight: '10px',
            },
            '& .MuiTablePagination-input .MuiSelect-select': {
              paddingTop: '2px',
              paddingBottom: '2px',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
            },
            '& .MuiTablePagination-input .MuiSvgIcon-root': {
              fill: 'white'
            },
            '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-actions': {
              marginLeft: '10px !important',
            },
            '& .MuiTablePagination-actions .MuiButtonBase-root': {
              color: '#FF69B4',
            },
            '& .MuiDataGrid-filler': {
              height: 'auto !important',
            },
            '& .MuiDataGrid-filler>div': {
              borderColor: 'transparent',
            },
            '& .MuiDataGrid-overlayWrapper': {
              height: 'auto !important',
              maxHeight: '50px',
            },
            '& .MuiDataGrid-overlayWrapperInner': {
              minHeight: '40px',
              height: 'auto !important'
            },
            '& .MuiDataGrid-overlay': {
              color: '#fff',
              borderTop: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'transparent',
              minHeight: '40px',
            },
            '& .MuiDataGrid-row .MuiDataGrid-cellOffsetLeft + .MuiDataGrid-cell + .MuiDataGrid-cell': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            },
          }}
        />
      </Box>

      <Dialog open={modalOpen} onClose={handleCloseModal} fullWidth maxWidth="sm">
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {editingPodcaster ? t('podcastersTranslation.popup_titles.edit_podcaster', 'Edit Podcaster') : t('podcastersTranslation.popup_titles.add_podcaster', 'Add Podcaster')}
          <IconButton onClick={handleCloseModal} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            overflowY: 'auto',
          }}
        >
          <DialogContent
            dividers
            sx={{
              borderColor: 'rgba(230, 210, 249, 0.1)',
              padding: '24px 16px',
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(255, 255, 255, 0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(230, 210, 249, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(230, 210, 249, 0.5)',
                },
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column" }}>

              <Controller
                name="name"
                control={control}
                rules={{
                  required: t('podcastersTranslation.validations.name_required', 'Name is required'),
                  minLength: { value: 3, message: t('podcastersTranslation.validations.name_min_length', 'Minimum 3 characters') }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.name}
                    helperText={errors.name?.message}
                    placeholder={t('podcastersTranslation.fields.name', 'Name')}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 20px',
                        fontWeight: '400',
                        color: '#fff',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="email"
                control={control}
                rules={{
                  required: t('podcastersTranslation.validations.email_required', 'Email is required'),
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: t("podcastersTranslation.validations.invalid_email", "Invalid email address")
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    placeholder={t('podcastersTranslation.fields.email', 'Email')}
                    autoComplete="new-email"
                    inputProps={{ autoComplete: "new-email" }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 20px',
                        fontWeight: '400',
                        color: '#fff',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="phone"
                control={control}
                rules={{
                  required: t('podcastersTranslation.validations.phone_required', 'Phone is required'),
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: t('podcastersTranslation.validations.phone_length', 'Enter valid 10 digit phone')
                  }
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '').slice(0, 10);
                      field.onChange(numericValue);
                    }}
                    onKeyDown={(e) => {
                      if (
                        [
                          'Backspace',
                          'Delete',
                          'Tab',
                          'Escape',
                          'Enter',
                          'ArrowLeft',
                          'ArrowRight',
                          'ArrowUp',
                          'ArrowDown',
                          'Home',
                          'End',
                        ].includes(e.key) ||
                        (e.ctrlKey || e.metaKey)
                      ) {
                        return;
                      }
                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                    error={!!errors.phone}
                    helperText={errors.phone?.message}
                    placeholder={t('podcastersTranslation.fields.phone', 'Phone')}
                    autoComplete="new-phone"
                    inputProps={{
                      autoComplete: "new-phone",
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      maxLength: 10
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 20px',
                        fontWeight: '400',
                        color: '#fff',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              {/* Password */}
              <Controller
                name="password"
                control={control}
                rules={{
                  required: !editingPodcaster
                    ? t('podcastersTranslation.validations.password_required', "Password is required")
                    : false,

                  minLength: {
                    value: 8,
                    message: t(
                      'podcastersTranslation.validations.password_min_length',
                      "Password must be at least 8 characters"
                    ),
                  },

                  pattern: {
                    value:
                      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]+$/,
                    message: t(
                      'podcastersTranslation.validations.password_pattern',
                      "Password must contain uppercase, lowercase, number and special character"
                    ),
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    type={showPassword ? "text" : "password"}
                    placeholder={t('podcastersTranslation.fields.password', 'Password')}
                    autoComplete="new-password"
                    inputProps={{ autoComplete: "new-password" }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowPassword(!showPassword)}>
                            {!showPassword ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                              <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                              <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                              <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                              <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                              <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                            </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                              <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                            </svg>}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 20px',
                        fontWeight: '400',
                        color: '#fff',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              {/* Confirm Password */}
              <Controller
                name="confirm_password"
                control={control}
                rules={{
                  required: !editingPodcaster ? t(
                    'podcastersTranslation.validations.confirm_password_required',
                    "Confirm password is required"
                  ) : false,

                  validate: (value) => {
                    // if password is empty during edit, no validation needed
                    if (!password) return true;

                    return (
                      value === password ||
                      t(
                        'podcastersTranslation.validations.password_not_matched',
                        "Passwords do not match"
                      )
                    );
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.confirm_password}
                    helperText={errors.confirm_password?.message}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder={t('podcastersTranslation.fields.confirm_password', 'Confirm Password')}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                            {!showConfirmPassword ? <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M9.46992 15.28C9.27992 15.28 9.08992 15.21 8.93992 15.06C8.11992 14.24 7.66992 13.15 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C13.1499 7.67004 14.2399 8.12004 15.0599 8.94004C15.1999 9.08004 15.2799 9.27004 15.2799 9.47004C15.2799 9.67004 15.1999 9.86004 15.0599 10L9.99992 15.06C9.84992 15.21 9.65992 15.28 9.46992 15.28ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 12.5 9.29992 12.98 9.53992 13.4L13.3999 9.54004C12.9799 9.30004 12.4999 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                              <path d="M5.59984 18.51C5.42984 18.51 5.24984 18.45 5.10984 18.33C4.03984 17.42 3.07984 16.3 2.25984 15C1.19984 13.35 1.19984 10.66 2.25984 8.99998C4.69984 5.17998 8.24984 2.97998 11.9998 2.97998C14.1998 2.97998 16.3698 3.73998 18.2698 5.16998C18.5998 5.41998 18.6698 5.88998 18.4198 6.21998C18.1698 6.54998 17.6998 6.61998 17.3698 6.36998C15.7298 5.12998 13.8698 4.47998 11.9998 4.47998C8.76984 4.47998 5.67984 6.41998 3.51984 9.80998C2.76984 10.98 2.76984 13.02 3.51984 14.19C4.26984 15.36 5.12984 16.37 6.07984 17.19C6.38984 17.46 6.42984 17.93 6.15984 18.25C6.01984 18.42 5.80984 18.51 5.59984 18.51Z" fill="#FF69B4" />
                              <path d="M12.0001 21.02C10.6701 21.02 9.37006 20.75 8.12006 20.22C7.74006 20.06 7.56006 19.62 7.72006 19.24C7.88006 18.86 8.32006 18.68 8.70006 18.84C9.76006 19.29 10.8701 19.52 11.9901 19.52C15.2201 19.52 18.3101 17.58 20.4701 14.19C21.2201 13.02 21.2201 10.98 20.4701 9.81C20.1601 9.32 19.8201 8.85 19.4601 8.41C19.2001 8.09 19.2501 7.62 19.5701 7.35C19.8901 7.09 20.3601 7.13 20.6301 7.46C21.0201 7.94 21.4001 8.46 21.7401 9C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02Z" fill="#FF69B4" />
                              <path d="M12.6901 16.27C12.3401 16.27 12.0201 16.02 11.9501 15.66C11.8701 15.25 12.1401 14.86 12.5501 14.79C13.6501 14.59 14.5701 13.67 14.7701 12.57C14.8501 12.16 15.2401 11.9 15.6501 11.97C16.0601 12.05 16.3301 12.44 16.2501 12.85C15.9301 14.58 14.5501 15.95 12.8301 16.27C12.7801 16.26 12.7401 16.27 12.6901 16.27Z" fill="#FF69B4" />
                              <path d="M1.99994 22.75C1.80994 22.75 1.61994 22.68 1.46994 22.53C1.17994 22.24 1.17994 21.76 1.46994 21.47L8.93994 14C9.22994 13.71 9.70994 13.71 9.99994 14C10.2899 14.29 10.2899 14.77 9.99994 15.06L2.52994 22.53C2.37994 22.68 2.18994 22.75 1.99994 22.75Z" fill="#FF69B4" />
                              <path d="M14.5302 10.2199C14.3402 10.2199 14.1502 10.1499 14.0002 9.99994C13.7102 9.70994 13.7102 9.22994 14.0002 8.93994L21.4702 1.46994C21.7602 1.17994 22.2402 1.17994 22.5302 1.46994C22.8202 1.75994 22.8202 2.23994 22.5302 2.52994L15.0602 9.99994C14.9102 10.1499 14.7202 10.2199 14.5302 10.2199Z" fill="#FF69B4" />
                            </svg> : <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                              <path d="M11.9999 16.33C9.60992 16.33 7.66992 14.39 7.66992 12C7.66992 9.61004 9.60992 7.67004 11.9999 7.67004C14.3899 7.67004 16.3299 9.61004 16.3299 12C16.3299 14.39 14.3899 16.33 11.9999 16.33ZM11.9999 9.17004C10.4399 9.17004 9.16992 10.44 9.16992 12C9.16992 13.56 10.4399 14.83 11.9999 14.83C13.5599 14.83 14.8299 13.56 14.8299 12C14.8299 10.44 13.5599 9.17004 11.9999 9.17004Z" fill="#FF69B4" />
                              <path d="M12.0001 21.02C8.24008 21.02 4.69008 18.82 2.25008 15C1.19008 13.35 1.19008 10.66 2.25008 8.99998C4.70008 5.17998 8.25008 2.97998 12.0001 2.97998C15.7501 2.97998 19.3001 5.17998 21.7401 8.99998C22.8001 10.65 22.8001 13.34 21.7401 15C19.3001 18.82 15.7501 21.02 12.0001 21.02ZM12.0001 4.47998C8.77008 4.47998 5.68008 6.41998 3.52008 9.80998C2.77008 10.98 2.77008 13.02 3.52008 14.19C5.68008 17.58 8.77008 19.52 12.0001 19.52C15.2301 19.52 18.3201 17.58 20.4801 14.19C21.2301 13.02 21.2301 10.98 20.4801 9.80998C18.3201 6.41998 15.2301 4.47998 12.0001 4.47998Z" fill="#FF69B4" />
                            </svg>}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiOutlinedInput-input': {
                        padding: '12px 20px',
                        fontWeight: '400',
                        color: '#fff',
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: '400', fontSize: '14px' }}>
                      {t("podcastersTranslation.fields.upload_profile_image", "Upload Profile Image")}
                    </Typography>

                    <Box
                      component="label"
                      onDrop={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        const file = e.dataTransfer.files[0];
                        if (file) {
                          handleImageChange({ target: { files: [file] } });
                        }
                      }}
                      onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
                      sx={{
                        mb: 2,
                        border: "1px dashed rgba(230, 210, 249, 0.2)",
                        p: 3,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        borderRadius: "999px",
                      }}
                    >
                      <input
                        hidden
                        type="file"
                        accept="image/png, image/jpg, image/webp"
                        onChange={handleImageChange}
                      />

                      {imagePreview ? (
                        <img
                          src={imagePreview}
                          alt="preview"
                          style={{
                            width: 80,
                            height: 80,
                            objectFit: "cover",
                            borderRadius: "50%",
                          }}
                        />
                      ) : (
                        <>
                          <Box sx={{ pointerEvents: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <CloudUpload fontSize="large" />
                            <Box>
                              <Typography variant="body1" sx={{ fontSize: '14px', my: '3px', textAlign: 'center' }}>
                                {t('podcastersTranslation.fields.drag_drop_here', "Upload Image (File must be less than 5MB)")}
                              </Typography>

                              <Typography variant="body2" sx={{ fontSize: '12px', color: "rgba(255,255,255,0.6)", textAlign: 'center' }}>
                                {t('podcastersTranslation.fields.click_browse', "or click to browse (Supported formats: PNG, JPG, WEBP)")}
                              </Typography>
                            </Box>
                          </Box>
                        </>
                      )}
                    </Box>

                    {/* Optional error */}
                    {errors.image && (
                      <Typography color="error" variant="caption">
                        {errors.image.message}
                      </Typography>
                    )}
                  </Grid>
                )}
              />

              <Controller
                name="bio"
                control={control}
                rules={{
                  required: t('podcastersTranslation.validations.bio_required', "Please fill bio"),
                  minLength: { value: 10, message: t('podcastersTranslation.validations.bio_min_length', "Minimum 10 characters required") },
                  maxLength: { value: 1000, message: t('podcastersTranslation.validations.bio_max_length', "Maximum 1000 characters allowed") },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    error={!!errors.bio}
                    helperText={
                      errors.bio?.message ||
                      t('podcastersTranslation.validations.bio_length_info', 'Min 10 characters • Max 1000 characters')
                    }
                    multiline
                    rows={3}
                    placeholder={t('podcastersTranslation.fields.bio', 'Bio')}
                    FormHelperTextProps={{
                      sx: {
                        color: '#ffffff',
                      },
                    }}
                    sx={{
                      mb: 2,
                      '& .MuiInputBase-root': {
                        padding: '0',
                      },
                      '& .MuiOutlinedInput-input': {
                        padding: '8px 20px 12px',
                        fontWeight: '400',
                        color: '#fff',
                        margin: '10px 0',
                        '&::-webkit-scrollbar': {
                          width: '4px',
                        },
                        '&::-webkit-scrollbar-track': {
                          background: 'rgba(255, 255, 255, 0.05)',
                          borderRadius: '4px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                          background: 'rgba(230, 210, 249, 0.3)',
                          borderRadius: '4px',
                        },
                      },
                      '& input::placeholder': {
                        color: 'rgba(255,255,255,0.6)',
                        opacity: 1,
                      },
                    }}
                  />
                )}
              />

              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Select
                      {...field}
                      sx={{
                        color: "#fff",
                        fontWeight: 400,
                        mb: 2,
                        "& .MuiSelect-select": {
                          padding: "12px 20px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1) !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#fff",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>{t('podcastersTranslation.fields.select_status', "Select Status")}</MenuItem>
                      <MenuItem value={true}>{t('active', 'Active')}</MenuItem>
                      <MenuItem value={false}>{t('inactive', 'Inactive')}</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />

              <Controller
                name="isVerified"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth>
                    <Select
                      {...field}
                      sx={{
                        color: "#fff",
                        fontWeight: 400,
                        "& .MuiSelect-select": {
                          padding: "12px 20px",
                        },
                        "& .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1) !important",
                        },
                        "&:hover .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        },
                        "& .MuiSvgIcon-root": {
                          color: "#fff",
                        },
                      }}
                    >
                      <MenuItem value="" disabled>{t('podcastersTranslation.fields.select_verification_status', "Select Verification Status")}</MenuItem>
                      <MenuItem value={true}>{t('verified', 'Verified')}</MenuItem>
                      <MenuItem value={false}>{t('pending', 'Pending')}</MenuItem>
                    </Select>
                  </FormControl>
                )}
              />
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
            <Button
              onClick={handleCloseModal}
              sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}
            >{t('cancel', 'Cancel')}</Button>
            <Button
              variant="contained"
              type="submit"
              disabled={submitting}
              sx={{
                py: 1.5,
                borderRadius: '999px',
                fontWeight: '400',
                fontSize: '15px',
                lineHeight: '20px',
                textTransform: 'none',
                background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                textTransform: 'capitalize',
                minWidth: { xs: '100%', sm: '150px' },
                marginLeft: '10px',
              }}
            >
              {submitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('save', 'Save')}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center', }}>{t('delete_podcaster', 'Delete Podcaster')}</DialogTitle>
        <DialogContent sx={{ pb: '0' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center', }}>{t('confirm_delete_podcaster', 'Are you sure you want to delete this Podcaster?')}</Typography>
        </DialogContent>
        <DialogActions
          sx={{
            px: '16px',
            py: '20px',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: '6px',
          }}
        >
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{
              backgroundColor: 'rgba(227, 214, 245, 0.2) !important',
              border: '1px solid rgba(227, 214, 245, 0.1)',
              fontSize: '15px',
              lineHeight: '20px',
              fontWeight: '400',
              padding: '11px 20px',
              minWidth: { xs: '100%', sm: '150px' },
              color: 'white',
              borderRadius: '999px',
              textTransform: 'capitalize',
            }}
          >{t('cancel', 'Cancel')}</Button>
          <Button
            variant="contained"
            disabled={deleting}
            onClick={handleDeleteConfirm}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              background:
                'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              textTransform: 'capitalize',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('delete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <GlobalStyles
        styles={{
          '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
            backgroundColor: '#252525 !important',
            border: '1px solid rgba(230, 210, 249, 0.1)',
            fontSize: '15px',
            color: '#fff',
            borderRadius: '4px',
          },
          '.MuiDialog-root .MuiPaper-root': {
            borderRadius: '20px',
          },
          '.MuiDataGrid-columnsManagementHeader': {
            padding: '10px !important',
          },
          '.MuiInputBase-formControl': {
            fontSize: '14px !important',
            color: 'white !important',
            borderRadius: '999px !important',
          },
          '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
            border: '1px solid rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
            borderTop: '1px solid rgba(230, 210, 249, 0.1)',
            padding: '4px 8px 4px 16px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
            padding: '0px 16px 12px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
            fontSize: '14px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
            color: '#FF69B4',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
            color: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
            borderColor: 'rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
            display: 'none',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
            padding: '0',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
            fontSize: '14px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
            padding: '5px 10px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: '#FF69B4',
          },
          '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
            borderColor: 'rgba(230, 210, 249, 0.3) !important',
            margin: '0',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            minWidth: '32px',
          },
        }}
      />
    </Box>
  );
}

export default PodcasterManagement;


